<template>
    <aside
        class="ticket-packages-offer-anchor"
        :class="[
            `ticket-packages-offer-anchor--${placement}`,
            { 'is-visible': visible }
        ]"
        :aria-hidden="visible ? 'false' : 'true'"
    >
        <!-- Editorial dock card — flush under sticky filter (mobile only) -->
        <button
            v-if="placement === 'dock'"
            type="button"
            class="ticket-packages-offer-anchor__bar"
            @click="$emit('cta')"
        >
            <span class="ticket-packages-offer-ribbon">
                <span
                    class="ticket-packages-offer-ribbon__icon"
                    aria-hidden="true"
                >
                    <svg
                        viewBox="0 0 16 16"
                        width="12"
                        height="12"
                        fill="currentColor"
                    >
                        <path
                            d="M2.2 3.2A1.2 1.2 0 0 1 3.4 2h5.1c.3 0 .6.1.8.4l3.5 3.5a1.2 1.2 0 0 1 0 1.7l-4.6 4.6a1.2 1.2 0 0 1-1.7 0L2.6 8.3a1.2 1.2 0 0 1-.4-.8V3.2zm3 1.3a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"
                        />
                    </svg>
                </span>
                <span
                    class="ticket-packages-offer-ribbon__sep"
                    aria-hidden="true"
                />
                <span class="ticket-packages-offer-ribbon__text">
                    {{ offerLabel }}
                </span>
            </span>

            <span class="ticket-packages-offer-anchor__bar-body">
                <span class="ticket-packages-offer-anchor__bar-left">
                    <span
                        v-if="title"
                        class="ticket-packages-offer-anchor__bar-title"
                        :class="
                            locationType
                                ? `ticket-packages-offer-anchor__bar-title--${locationType}`
                                : null
                        "
                    >
                        {{ title }}
                    </span>
                    <span
                        v-if="barContextParts.length"
                        class="ticket-packages-offer-anchor__bar-context"
                    >
                        <span
                            v-if="sectorLabel"
                            class="badge"
                            :class="
                                locationType ? `badge-${locationType}` : null
                            "
                        >
                            {{ sectorLabel }}
                        </span>
                        <span
                            v-if="sectorLabel && barDateLine"
                            class="ticket-packages-offer-anchor__bar-sep"
                            aria-hidden="true"
                        />
                        <span
                            v-if="barDateLine"
                            class="ticket-packages-offer-anchor__bar-date"
                        >
                            {{ barDateLine }}
                        </span>
                        <ticket-packages-type
                            v-if="ticketType"
                            :type="ticketType"
                            :trans="ticketTypeTrans"
                        />
                    </span>
                </span>

                <span class="ticket-packages-offer-anchor__bar-right">
                    <span
                        v-if="discountPercent"
                        class="ticket-packages-offer-anchor__bar-stat"
                    >
                        <span class="ticket-packages-offer-anchor__bar-stat-label">
                            {{ discountLabel }}
                        </span>
                        <span class="ticket-packages-offer-anchor__bar-discount">
                            -{{ discountPercent }}%
                        </span>
                    </span>

                    <span
                        v-if="discountPercent && priceOld"
                        class="ticket-packages-offer-anchor__bar-sep ticket-packages-offer-anchor__bar-sep--tall"
                        aria-hidden="true"
                    />

                    <span
                        v-if="priceOld"
                        class="ticket-packages-offer-anchor__bar-stat ticket-packages-offer-anchor__bar-stat--was"
                    >
                        <span class="ticket-packages-offer-anchor__bar-stat-label">
                            {{ wasLabel }}
                        </span>
                        <span class="ticket-packages-offer-anchor__bar-was">
                            {{ priceOld }}
                        </span>
                    </span>

                    <span
                        v-if="priceOld"
                        class="ticket-packages-offer-anchor__bar-sep ticket-packages-offer-anchor__bar-sep--tall"
                        aria-hidden="true"
                    />

                    <span
                        v-if="price"
                        class="ticket-packages-offer-anchor__bar-stat"
                    >
                        <span class="ticket-packages-offer-anchor__bar-stat-label">
                            {{ nowLabel }}
                        </span>
                        <strong class="ticket-packages-offer-anchor__bar-price">
                            {{ price }}
                        </strong>
                    </span>

                    <span
                        class="ticket-packages-offer-anchor__bar-cta"
                        aria-hidden="true"
                    >
                        <svg
                            viewBox="0 0 16 16"
                            width="16"
                            height="16"
                            fill="none"
                        >
                            <path
                                d="M3.5 8h9M8.5 4l4 4-4 4"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </span>
                </span>
            </span>
        </button>

        <!-- Map: ribbon on border + split (product | price) -->
        <div v-else class="ticket-packages-offer-anchor__shell">
            <button
                type="button"
                class="ticket-packages-offer-anchor__card"
                @click="$emit('cta')"
            >
                <span class="ticket-packages-offer-ribbon">
                    <span
                        class="ticket-packages-offer-ribbon__icon"
                        aria-hidden="true"
                    >
                        <svg
                            viewBox="0 0 16 16"
                            width="12"
                            height="12"
                            fill="currentColor"
                        >
                            <path
                                d="M8.1 1.4c.2 1.7-.4 2.8-1.4 3.8-.7.7-1.5 1.4-1.9 2.4-.5 1.3-.3 2.7.6 3.8.2-.8.7-1.5 1.4-2 .2 1.5 1 2.7 2.4 3.5 1.5-.7 2.5-2 2.9-3.6.5-1.8.1-3.6-1-5.1-.5 1.1-1.3 1.8-2.1 2.2.3-1.6.1-3.2-.9-5z"
                            />
                        </svg>
                    </span>
                    <span
                        class="ticket-packages-offer-ribbon__sep"
                        aria-hidden="true"
                    />
                    <span class="ticket-packages-offer-ribbon__text">
                        {{ offerLabel }}
                    </span>
                </span>

                <div class="ticket-packages-offer-anchor__content">
                    <div class="ticket-packages-offer-anchor__left">
                        <h3
                            class="ticket-packages-offer-anchor__title"
                            :class="
                                locationType
                                    ? `ticket-packages-offer-anchor__title--${locationType}`
                                    : null
                            "
                        >
                            {{ title }}
                        </h3>

                        <div
                            v-if="sectorLabel || ticketType"
                            class="ticket-packages-offer-anchor__tags"
                        >
                            <span
                                v-if="sectorLabel"
                                class="badge"
                                :class="
                                    locationType
                                        ? `badge-${locationType}`
                                        : null
                                "
                            >
                                {{ sectorLabel }}
                            </span>
                            <ticket-packages-type
                                v-if="ticketType"
                                :type="ticketType"
                                :trans="ticketTypeTrans"
                            />
                        </div>

                        <div
                            v-if="date || dayOfWeek || group"
                            class="ticket-packages-offer-anchor__info"
                        >
                            <span
                                v-if="date || dayOfWeek"
                                class="ticket-packages-offer-anchor__date"
                            >
                                <template v-if="date">{{ date }}</template>
                                <template v-if="date && dayOfWeek">
                                    •
                                </template>
                                {{ dayOfWeek }}
                            </span>
                            <span
                                v-if="group"
                                class="ticket-packages-offer-anchor__group"
                            >
                                {{ group }}
                            </span>
                        </div>
                    </div>

                    <div class="ticket-packages-offer-anchor__right">
                        <span
                            v-if="urgencyLabel || offerLabel"
                            class="ticket-packages-offer-limited"
                        >
                            <i
                                v-if="dealIconClass"
                                class="fa ticket-packages-offer-limited__icon"
                                :class="dealIconClass"
                                aria-hidden="true"
                            />
                            {{ urgencyLabel || offerLabel }}
                        </span>

                        <div class="ticket-packages-offer-anchor__pricing">
                            <ticket-packages-price
                                v-if="price"
                                :price="price"
                                :from="priceOld"
                                :featured="true"
                                :discount-percent="discountPercent"
                                :trans="priceTrans"
                            />
                            <span
                                v-if="personLabel"
                                class="ticket-packages-offer-anchor__per-person"
                            >
                                {{ personLabel }}
                            </span>
                        </div>

                        <span class="ticket-packages-offer-anchor__cta">
                            {{ ctaLabel }}
                        </span>
                    </div>
                </div>
            </button>
        </div>
    </aside>
</template>

<script>
import TicketPackagesPrice from "./TicketPackagesPrice.vue";
import TicketPackagesType from "./TicketPackagesType.vue";

export default {
    name: "TicketPackagesOfferAnchor",
    components: {
        TicketPackagesPrice,
        TicketPackagesType
    },
    props: {
        placement: {
            type: String,
            required: true,
            validator(value) {
                return value === "map" || value === "dock" || value === "none";
            }
        },
        visible: {
            type: Boolean,
            required: false,
            default: false
        },
        offerLabel: {
            type: String,
            required: false,
            default: "Today's offer"
        },
        urgencyLabel: {
            type: String,
            required: false,
            default: null
        },
        dealIconCode: {
            type: [Number, String],
            required: false,
            default: null
        },
        title: {
            type: String,
            required: true
        },
        sectorLabel: {
            type: String,
            required: false,
            default: null
        },
        locationType: {
            type: String,
            required: false,
            default: null
        },
        ticketType: {
            type: String,
            required: false,
            default: null,
            validator(value) {
                if (value == null || value === "") return true;
                return (
                    String(value).indexOf("physical") !== -1 ||
                    String(value).indexOf("digital") !== -1
                );
            }
        },
        ticketTypeTrans: {
            type: Object,
            required: false,
            default() {
                return {};
            }
        },
        date: {
            type: String,
            required: false,
            default: null
        },
        dayOfWeek: {
            type: String,
            required: false,
            default: null
        },
        group: {
            type: String,
            required: false,
            default: null
        },
        includes: {
            type: Array,
            required: false,
            default() {
                return [];
            }
        },
        includesLabel: {
            type: String,
            required: false,
            default: "Includes package"
        },
        offerTitle: {
            type: String,
            required: false,
            default: null
        },
        offerDescription: {
            type: String,
            required: false,
            default: null
        },
        price: {
            type: String,
            required: false,
            default: null
        },
        priceOld: {
            type: String,
            required: false,
            default: null
        },
        discountPercent: {
            type: Number,
            required: false,
            default: null
        },
        priceTrans: {
            type: Object,
            required: false,
            default() {
                return {};
            }
        },
        ctaLabel: {
            type: String,
            required: false,
            default: "See details"
        }
    },
    computed: {
        personLabel() {
            const details =
                (this.priceTrans && this.priceTrans.details) || {};
            const person = String(details.person || "").trim();
            if (!person) return null;
            return `per ${person}`;
        },
        barDateLine() {
            const date = String(this.date || "").trim();
            const day = String(this.dayOfWeek || "").trim();
            if (date && day) return `${date} • ${day}`;
            return date || day || null;
        },
        barContextParts() {
            const parts = [];
            if (this.sectorLabel) parts.push("sector");
            if (this.barDateLine) parts.push("date");
            if (this.ticketType) parts.push("type");
            return parts;
        },
        discountLabel() {
            return (
                (this.priceTrans && this.priceTrans.discount) || "Discount"
            );
        },
        wasLabel() {
            return (this.priceTrans && this.priceTrans.was) || "Was";
        },
        nowLabel() {
            return (this.priceTrans && this.priceTrans.now) || "Now";
        },
        dealIconClass() {
            // Same icon map as list offer badge / Console DealIconPicker (1–6).
            const code = Number(this.dealIconCode);
            const iconMap = {
                1: "fa-usd",
                2: "fa-bolt",
                3: "fa-calendar-check-o",
                4: "fa-star",
                5: "fa-exclamation",
                6: "koko-deal"
            };
            return iconMap[code] || null;
        }
    }
};
</script>

<style lang="scss" scoped>
@import "../../../../sass/variables";

$ease-offer: cubic-bezier(0.32, 0.72, 0, 1);

.ticket-packages-offer-anchor {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-8px);
    transition: opacity 0.45s $ease-offer, transform 0.45s $ease-offer;
    will-change: transform, opacity;

    &.is-visible {
        opacity: 1;
        pointer-events: auto;
        transform: translateY(0);
    }

    // —— Dock: bottom chrome on mobile (replaces header-mobile while visible) ——
    &--dock {
        position: fixed;
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 95; // below filter (100); above page content
        // Top pad clears ribbon; bottom pad + safe-area sit above home indicator
        padding: 14px 12px calc(12px + env(safe-area-inset-bottom, 0px));
        background: transparent;
        transform: translateY(16px);

        @media (min-width: 800px) {
            display: none;
        }
    }

    &--dock.is-visible {
        transform: translateY(0);
    }

    &__bar {
        position: relative;
        display: block;
        width: 100%;
        margin: 0;
        padding: 18px 12px 12px;
        overflow: visible;
        border: 1.5px solid rgba($color-red, 0.55);
        border-radius: 12px;
        background: #ffffff;
        box-shadow:
            0 1px 0 rgba(227, 52, 47, 0.04),
            0 10px 24px rgba(17, 0, 36, 0.08);
        color: $color2-text-primary;
        text-align: left;
        cursor: pointer;
        appearance: none;
        transition: transform 0.45s $ease-offer, border-color 0.45s $ease-offer,
            box-shadow 0.45s $ease-offer;

        &:hover {
            border-color: $color-red;
            box-shadow:
                0 1px 0 rgba(227, 52, 47, 0.06),
                0 14px 28px rgba(17, 0, 36, 0.1);
        }

        &:active {
            transform: scale(0.985);
        }

        &:focus {
            outline: none;
            box-shadow:
                0 0 0 3px rgba($color-red, 0.18),
                0 10px 24px rgba(17, 0, 36, 0.08);
        }
    }

    &__bar-body {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        width: 100%;
        min-width: 0;
    }

    &__bar-left {
        display: flex;
        flex-direction: column;
        gap: 6px;
        min-width: 0;
        flex: 1 1 auto;
    }

    &__bar-title {
        display: block;
        font-size: 18px;
        font-weight: 800;
        line-height: 1.1;
        letter-spacing: -0.02em;
        color: $color2-text-primary;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        // Same product colors as list card titles / map offer title
        @each $location, $color in $color2-locations-colors {
            &--#{$location} {
                color: $color;
            }
        }
    }

    &__bar-context {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        gap: 8px;
        min-width: 0;
        overflow: hidden;
    }

    &__bar-context ::v-deep .badge {
        flex-shrink: 0;
        padding: 3px 7px;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.2;
        border-radius: 5px;
        white-space: nowrap;
    }

    &__bar-date {
        flex-shrink: 0; // never crush to a single glyph (looked like a stray "F")
        color: $color2-text-secondary;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.2;
        white-space: nowrap;
    }

    &__bar-sep {
        flex-shrink: 0;
        width: 1px;
        height: 12px;
        background: rgba(17, 0, 36, 0.14);

        &--tall {
            align-self: stretch;
            height: auto;
            min-height: 28px;
            margin: 0 2px;
            margin-top: 0;
        }
    }

    &__bar-right {
        display: flex;
        align-items: flex-start; // top-align Discount / Was / Now / CTA
        gap: 10px;
        flex-shrink: 0;
        padding-left: 12px;
        border-left: 1px solid rgba(17, 0, 36, 0.12);
    }

    &__bar-stat {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: 4px;
        min-width: 0;
    }

    &__bar-stat-label {
        display: block;
        height: 10px;
        font-size: 9px;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        line-height: 10px;
        color: $color2-text-secondary;
        white-space: nowrap;
    }

    &__bar-discount {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-height: 20px;
        padding: 3px 7px;
        border-radius: 5px;
        background: $color-red;
        color: #fff;
        font-size: 12px;
        font-weight: 800;
        line-height: 1;
        white-space: nowrap;
    }

    &__bar-was {
        display: inline-flex;
        align-items: center;
        min-height: 20px;
        font-size: 13px;
        font-weight: 500;
        line-height: 1;
        color: $color2-text-secondary;
        text-decoration: line-through;
        white-space: nowrap;
    }

    &__bar-price {
        display: inline-flex;
        align-items: center;
        min-height: 20px;
        font-size: 20px;
        font-weight: 800;
        letter-spacing: -0.03em;
        color: $color-red;
        line-height: 1;
        white-space: nowrap;
    }

    &__bar-cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        align-self: center; // keep arrow optically centered vs. price block
        width: 36px;
        height: 36px;
        border-radius: 8px;
        background: $color2-primary;
        color: #fff;
        flex-shrink: 0;
        transition: transform 0.45s $ease-offer, background-color 0.45s $ease-offer;
    }

    &__bar:hover &__bar-cta {
        background: darken($color2-primary, 4%);
        transform: translateX(2px);
    }

    // Narrow phones: drop secondary chrome so sector + date stay readable
    @media (max-width: 420px) {
        &__bar-stat-label {
            display: none;
        }

        &__bar-right {
            gap: 8px;
            padding-left: 10px;
        }

        &__bar-price {
            font-size: 18px;
        }

        &__bar-title {
            font-size: 16px;
        }

        &__bar-stat--was,
        &__bar-sep--tall {
            display: none;
        }

        // Ticket-type badge is the squeeze culprit — hide before the date
        &__bar-context ::v-deep .ticket-type {
            display: none;
        }

        &__bar-context {
            gap: 6px;
        }
    }

    // —— Map: ribbon on border + split (product | price) ——
    &--map {
        margin-top: 18px;
        width: 100%;
    }

    &__shell {
        width: 100%;
        padding-top: 10px;
    }

    &__card {
        position: relative;
        display: block;
        width: 100%;
        margin: 0;
        padding: 0;
        border: 1.5px solid rgba(227, 52, 47, 0.55);
        border-radius: 12px;
        background: #ffffff;
        color: $color2-text-primary;
        text-align: left;
        cursor: pointer;
        appearance: none;
        box-shadow: 0 8px 20px rgba(17, 0, 36, 0.06);
        overflow: visible;
        transition: border-color 0.55s $ease-offer, box-shadow 0.55s $ease-offer,
            transform 0.55s $ease-offer;

        &:hover {
            border-color: $color-red;
            transform: translateY(-2px);
            box-shadow: 0 14px 28px rgba(17, 0, 36, 0.1);
        }

        &:active {
            transform: scale(0.985);
        }

        &:focus {
            outline: none;
            box-shadow: 0 0 0 3px rgba($color-red, 0.18);
        }
    }

    &__content {
        display: grid;
        grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr);
        align-items: stretch;
        min-width: 0;
    }

    &__left {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 8px;
        min-width: 0;
        padding: 18px 14px 16px 16px;
    }

    &__right {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        min-width: 0;
        padding: 16px 14px 14px;
        border-left: 1px solid rgba(17, 0, 36, 0.1);
        text-align: center;

        .ticket-packages-offer-limited {
            margin-bottom: 0;
            align-self: center;
            padding: 4px 10px;
            font-size: 11px;
        }
    }

    &__title {
        margin: 0;
        font-size: 20px;
        font-weight: 800;
        line-height: 1.1;
        letter-spacing: -0.02em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        color: $color2-text-primary;

        @each $location, $color in $color2-locations-colors {
            &--#{$location} {
                color: $color;
            }
        }
    }

    &__tags {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        gap: 6px;
        min-width: 0;
        overflow: hidden;
    }

    ::v-deep .badge {
        flex-shrink: 0;
        white-space: nowrap;
    }

    &__info {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        align-items: center;
        gap: 6px;
        width: 100%;
        min-width: 0;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.2;
        color: $color2-text-secondary;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__date {
        flex: 0 1 auto;
        min-width: 0;
        color: inherit;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    &__group {
        flex-shrink: 0;
        padding-left: 8px;
        border-left: 1px solid rgba(17, 0, 36, 0.18);
        color: $color2-text-secondary;
        white-space: nowrap;
    }

    &__pricing {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
        width: 100%;
        margin-top: auto;
    }

    ::v-deep .ticket-packages-price {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        padding: 0;
        margin: 0;
        min-width: 0;

        .ticket-packages-price__was {
            display: flex;
            width: 100%;
            justify-content: center;
            align-items: center;
            gap: 6px;
            margin-bottom: 2px;
        }

        .price {
            display: block;
            width: 100%;
            text-align: center;
            font-size: 26px;
            font-weight: 800;
            line-height: 1;
            letter-spacing: -0.03em;
        }

        .discount-badge {
            padding: 2px 7px;
            border-radius: 999px;
            font-size: 11px;
        }
    }

    &__per-person {
        font-size: 11px;
        font-weight: 500;
        line-height: 1.2;
        color: $color2-text-secondary;
    }

    &__cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 36px;
        padding: 0 14px;
        border-radius: 999px;
        background: $color2-primary;
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        line-height: 1.2;
        white-space: nowrap;
        pointer-events: none;
        transition: transform 0.45s $ease-offer, background-color 0.45s $ease-offer;
    }

    &__card:hover &__cta {
        background: darken($color2-primary, 4%);
    }

    &__card:active &__cta {
        transform: scale(0.98);
    }
}
</style>
